import { useCallback, useContext, useEffect, useState } from 'react';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { CustomerProduct } from '../../clients/customer-api/product/customer-product';
import { TenantContext } from '../../context/tenant-context';
import { Typography } from '../../components/typography/typography';
import { displayMoneyFormat } from '../../util/util';
import { Location } from '../../clients/quipli-service/location/location';
import { OrderType } from '../../clients/quipli-service/order/order';
import {
  CalculateSalePriceRequest,
  CalculateSalePriceResult,
  RentalProductPrice,
} from '../../clients/quipli-service/pricing/pricing';
import { toast } from '../../util/toasts';
import { QuantityInput } from '../product-form/quantity-input/quantity-input';
import { ProductImageV2 } from '../../components/image/product/product-image-v2';
import { EcommerceUser, ProductAddonSelection } from './product-details/product-details';
import classNames from 'classnames';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';
import { CustomerContext } from '../../context/customer-context';

export const AddOnProducts = ({
  orderType,
  addOns,
  selectedAddOns,
  hidePricesNotLoggedIn,
  location,
  user,
  refreshAddons,
  taxInclusive,
  taxLabel,
}: {
  orderType: OrderType;
  addOns: CustomerProduct[];
  selectedAddOns: ProductAddonSelection;
  hidePricesNotLoggedIn: boolean;
  location: Location & { urlAlias: string };
  user: EcommerceUser;
  refreshAddons: (addonsSelection: ProductAddonSelection, getAvailability: boolean) => void;
  taxInclusive: boolean;
  taxLabel?: string;
}) => {
  const { contextPath } = useContext(TenantContext);
  const customer = useContext(CustomerContext);
  const [rentalPrices, setRentalPrices] = useState<Map<number, RentalProductPrice[]>>(new Map());
  const [addOnSalePrices, setAddOnSalePrices] = useState<CalculateSalePriceResult[]>([]);
  const [displayedAddons, setDisplayedAddons] = useState<CustomerProduct[]>(addOns);
  const { calculatePrices, listPricesForMultipleProducts } = useCustomerAPI();
  const init = useCallback(
    async (addOns: CustomerProduct[]) => {
      const saleAddOns = addOns
        .filter((addOn) => addOn.allowSale)
        .map((addOn) => {
          return {
            type: OrderType.Sale,
            productId: addOn.id,
            locationId: location.id,
            userId: user.userId,
          };
        });
      let salePrices: CalculateSalePriceResult[] = [];
      if (saleAddOns.length > 0) {
        salePrices = (await calculatePrices(saleAddOns as CalculateSalePriceRequest[]).catch((e) => {
          console.log(e);
          toast('Error calculating add-on prices', 'error');
          return [];
        })) as CalculateSalePriceResult[];
      }

      setAddOnSalePrices(salePrices as CalculateSalePriceResult[]);
      const rp = await listPricesForMultipleProducts(
        addOns.map((a) => a.id),
        location.id,
        OrderType.Rental,
        user.userId,
      );
      setRentalPrices(rp as Map<number, RentalProductPrice[]>);
    },
    [addOns, location.id],
  );

  useEffect(() => {
    init(addOns);
  }, [addOns]);

  useEffect(() => {
    setDisplayedAddons(
      orderType === OrderType.Rental
        ? addOns.filter((addOn) => addOn.locationIds.includes(location.id))
        : addOns.filter((addOn) => addOn.locationIds.includes(location.id) && addOn.allowSale),
    );
  }, [orderType, addOns, location.id]);

  const renderAddOnRow = (addOn: CustomerProduct) => {
    const addonPrice = addOnSalePrices.find((r) => r.productId === addOn.id);
    const salePrice = addonPrice?.totalPrice
      ? addonPrice.totalPrice * (taxInclusive ? 1 + (addonPrice?.taxPercentage ?? 0) / 100 : 1)
      : undefined;
    const selection = selectedAddOns[addOn.id]!;
    const stock = selection.stock?.find((s) => s.locationId === location.id);
    const [saleStock, rentalStock] = [
      stock?.saleStock ?? addOn.saleStock,
      selection.maxQuantityAvailable ?? stock?.rentalStock,
    ];
    const unavailable =
      addOn.trackingType === ProductTrackingType.NonInventory
        ? false
        : (!selection.isAvailable && selection.quantity <= 1) ||
          (orderType === 'Sale' && addOn.allowSale && !salePrice) ||
          (orderType === 'Rental' && (addOn.allowRent ? rentalStock : saleStock) === 0);

    return (
      <div className={`addon-row grid grid-cols-11 gap-2 items-center p-2 sm:p-4 ${unavailable ? 'disabled' : ''}`}>
        <div className="col-span-2">
          <a
            href={`${contextPath}${location.urlAlias}/${addOn.urlKeyword}`}
            className="bg-transparent no-underline decoration-transparent hover:color-transparent sm:w-[59px] mr-4 sm:h-[59px] border border-solid rounded-sm justify-center flex border-grey-300 items-center w-full h-full"
          >
            <ProductImageV2
              className="w-max-full sm:h-[47px] sm:w-[47px]"
              productId={addOn.id}
              profile="MINI"
              alt={addOn.altTitles ? addOn.altTitles[0] : ''}
            />
          </a>
        </div>
        <div className="col-span-4 md:flex md:justify-between lg:block">
          <Typography type="text-xs" weight="bold">
            {addOn.name}
          </Typography>
          <div className="prices">
            {hidePricesNotLoggedIn ? (
              <Typography type="text-xs">Sign in to see price</Typography>
            ) : (addOn.allowRent && addOn.hidePrices) || (!addOn.allowRent && addOn.hideSalePrices) ? (
              <Typography type="text-xs">Contact for pricing</Typography>
            ) : (
              <>
                {orderType === OrderType.Rental && addOn.allowRent ? (
                  <>
                    {rentalPrices
                      .get(addOn.id)
                      ?.filter((rp) => rp.featured)
                      .map((price) => (
                        <Typography type="text-xs">
                          {displayMoneyFormat(
                            (price.price * (taxInclusive ? 1 + (price.taxPercentage ?? 0) / 100 : 1)) / 100,
                            location.currency,
                          )}{' '}
                          Per {price.durationLabel}{' '}
                          {taxInclusive
                            ? addOn.taxable && (customer ? customer.chargeTax : true)
                              ? ` incl. ${taxLabel}`
                              : ` no ${taxLabel} incl.`
                            : ''}
                        </Typography>
                      ))}
                  </>
                ) : addOn.allowSale ? (
                  <Typography type="text-xs">
                    {salePrice ? displayMoneyFormat(salePrice / 100, location.currency) : 'N/A'}{' '}
                    {taxInclusive
                      ? addOn.taxable && (customer ? customer.chargeTax : true)
                        ? ` incl. ${taxLabel}`
                        : ` no ${taxLabel} incl.`
                      : ''}
                  </Typography>
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </div>

        <div className="col-span-4">
          <div className={`quantity-wrapper-wrapper ${unavailable ? 'disabled' : ''}`}>
            <QuantityInput
              name="addonq"
              min={selection.minimumOrderQuantity}
              max={
                addOn.trackingType === ProductTrackingType.NonInventory
                  ? undefined
                  : orderType === 'Rental' && selection.allowRent
                  ? rentalStock
                  : saleStock
              }
              increment={selection.quantityIncrement}
              value={(selection.quantity || selection.minimumOrderQuantity)?.toString()}
              onChange={async (quantity) => {
                const quantityNumber = parseFloat(quantity as any);

                refreshAddons(
                  {
                    ...selectedAddOns,
                    [addOn.id]: {
                      ...selection,
                      quantity: quantity !== undefined ? quantityNumber : selectedAddOns[addOn.id].quantity,
                      selected: quantity === undefined ? false : selectedAddOns[addOn.id].selected,
                    },
                  },
                  addOn.trackingType !== ProductTrackingType.NonInventory,
                );
              }}
            />

            {!selection.isAvailable && (
              <Typography
                type="text-xs"
                className={classNames({
                  'text-grey-400': unavailable,
                  'text-warning-500': !unavailable,
                })}
              >
                {unavailable ? 'No stock available' : `${selection.quantity} not available`}
              </Typography>
            )}
          </div>
        </div>

        <div className="col-span-1 justify-self-center ">
          <input
            id={`addOn-[${addOn.id}]`}
            className={`col-span-1 scale-150  accent-[var(--primary-color-500)] ${
              unavailable ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            type="checkbox"
            checked={unavailable ? false : selection.selected}
            onChange={() =>
              refreshAddons(
                {
                  ...selectedAddOns,
                  [addOn.id]: { ...selection, selected: !selection.selected },
                },
                false,
              )
            }
            disabled={unavailable}
          />
        </div>
      </div>
    );
  };

  return addOns.length === 0 ? (
    <></>
  ) : (
    <div className="my-4">
      <Typography type="text-sm">Items typically ordered with this product</Typography>
      <div className="mt-4 addons">{displayedAddons.map((addOn) => renderAddOnRow(addOn))}</div>
    </div>
  );
};
